import * as ko from "knockout";
import {IObjectType, ISearchParams, ISyncField} from "../../../index";
import {IAvailableData, TColumnsTranslation} from "./index";
import {AvailableLanguages, ILanguage} from "../../../constants/Language";
import Abstract from "./Abstract";
import {nsSystemfields} from "../nsSystemfields";
import {ObjectTypeId} from "../../../constants";
import {Group} from "./group";
import {Table} from "./table";
import {ErrorObject} from "../../../error/ErrorObject";
import {_} from 'lodash';
import {nsSyncFields} from "../../../req/nsSyncFields";
import {lm} from "../../../appContext";
import {NO_DATA_MESSAGE} from "../../../constants/Language";
import {IComboboxV2Options} from "../../combobox/v2";
import {createApiListPromise, createBaseTextMatch} from "../../combobox/v2-sources";
import itemHtml from './item.html?raw';

export const template = itemHtml;

export class Item extends Abstract {
    public readonly languageid: number;
    private readonly language: ILanguage;

    private readonly AvailableFieldTypes = nsSystemfields.AvailableFieldTypes

    constructor(syncfield: ISyncField, availableData: IAvailableData, table: Table, private group: Group) {
        super(syncfield, availableData, table)
        this.languageid = syncfield.languageid
        this.language = AvailableLanguages.find(o => o.id == this.languageid)!
        if (this.group.mode() == 'create') {
            this.init()
        }
        this.mode = this.group.mode
    }

    init() {
        const self = this as any;
        const addObs = (key: TColumnsTranslation) => {
            self[key] = ko.observable(this.syncfield[key])
        }
        const addErr = (key: TColumnsTranslation) => {
            const errKey = 'err_' + key;
            const errObj = self[errKey] = new ErrorObject();
            errObj.addValidator({
                fn: () => !!self[key](),
                message: 'Invalid value',
            })
            this.errorManager.addErrorObject(errObj)
            return errObj
        }
        this.availableData.columnsTranslation().forEach(key => {
            switch (key) {
                case "core":
                case "syncfielddisplaynameid":
                    addObs(key)
                    // no validation needed
                    break;
                case "description":
                case "displayname":
                    addObs(key)
                    break;
                case "parameters":
                    addObs(key)
                    addErr(key)
                    break;
                case "fieldtype": {
                    const fieldtype = (() => {
                        const fieldtype = this.syncfield.fieldtype || 'String';
                        return nsSystemfields.AvailableFieldTypes.find(type => type.toLocaleLowerCase() == fieldtype.toLocaleLowerCase())
                    })()

                    let errObj: ErrorObject

                    addObs(key)
                    errObj = addErr(key)

                    addObs('referenceobjecttypeid')
                    errObj.addValidator({
                        fn: () => !!self.referenceobjecttypeid(),
                        message: 'invalid value',
                        prerequisite: () => self.fieldtype() == 'Reference',
                    })

                    addObs('referencefieldid')
                    errObj.addValidator({
                        fn: () => !!self.referencefieldid(),
                        message: 'invalid value',
                        prerequisite: () => self.fieldtype() == 'Reference' && !!self.referenceobjecttypeid(),
                    })
                    break;
                }
            }
        })
    }

    uninit() {
        super._uninit(this.availableData.columnsTranslation())
    }

    /*******************************************************************************************************************
     * View helpers
     ******************************************************************************************************************/

    filterSearchKey(searchkey: string): boolean {
        if (this.isMode('read') && this.syncfield.syncfielddisplaynameid === 0) {
            return false
        }
        return !!this.availableData.columnsTranslation().find((key: TColumnsTranslation) => {
            let val: any = this.getDef(key).content(this);
            let ret: boolean = String(val).toLowerCase().includes(searchkey)
            if (ret) {
                return ret
            }
            if (key == 'fieldtype' && val == 'Reference') {
                val = this.getDef('referencefieldid').content(this)
                ret = String(val).includes(searchkey)
                if (ret) {
                    return ret
                }
                val = this.getDef('referenceobjecttypeid').content(this)
                ret = String(val).includes(searchkey)
                if (ret) {
                    return ret
                }
            }
        });
    }

    isChanged(): boolean {
        const a = this.isFieldChanged('displayname')
        const b = this.isFieldChanged('description')
        const c = this.isFieldChanged('core')
        const d = this.isFieldChanged('fieldtype')
        const e = this.isFieldChanged('referenceobjecttypeid')
        const f = this.isFieldChanged('referencefieldid')
        const g = this.isFieldChanged('parameters')
        return a || b || c || d || e || f || g
    }

    getSortValue(): string | number {
        const col = this.table.sortcol().split('_')[0] as keyof ISyncField
        return this.getDef(col).content(this)
    }

    getAvailableObjectTypes(): IObjectType[] {
        return this.availableData.objectTypes.filter(o => [ObjectTypeId.User, ObjectTypeId.Department, ObjectTypeId.Group].includes(o.id))
    }

    private getSyncfieldsParams(objecttypeid: ObjectTypeId) {
        return nsSyncFields.getSyncfieldsRequestParams(objecttypeid)
    }

    /** Reference syncfield picker when fieldtype is Reference (legacy base → v2). */
    private getReferenceFieldComboboxOptions(): IComboboxV2Options<ISyncField> {
        const self = this as any
        const labelFn = (o: ISyncField) => o.name
        const apiList = createApiListPromise<ISyncField>({
            api: 'getsyncfields',
            isSearch: false,
            params: this.getSyncfieldsParams(self.referenceobjecttypeid()) as unknown as Partial<ISearchParams>,
        })
        return {
            promise: apiList.promise,
            onDispose: apiList.onDispose,
            emptyPreparedListMessage: NO_DATA_MESSAGE,
            inputClass: 'combobox-referencefieldid',
            inputAttrs: {placeholder: lm.t('choose|td') ?? ''},
            initialMatch: o => o.syncfieldid == self.referencefieldid(),
            option: {
                label: labelFn,
                match: createBaseTextMatch(labelFn),
                tooltip: o => {
                    const title = o.description
                    return title == null ? labelFn(o) : String(title)
                },
            },
            prepareData: {
                sort: (items) => _.sortBy(items, (o: ISyncField) => o.name.toLowerCase()),
            },
            onSelect: (o) => {
                if (o != null) {
                    self.referencefieldid(o.syncfieldid)
                }
            },
        }
    }

    /*******************************************************************************************************************
     * Misc.
     ******************************************************************************************************************/

    public getSubmitData(): ISyncField {
        const self: any = this
        const ret: ISyncField = _.cloneDeep(this.group.syncfield)

        if (!ret.fieldtype) {
            ret.fieldtype = 'String'
        }

        this.availableData.columnsTranslation().forEach((k: TColumnsTranslation) => {
            if (k in self) {
                const v = self[k]
                let val = ko.isObservable(v) ? v() : v
                if (typeof val === 'string') {
                    val = val.trim()
                }
                (ret as any)[k] = val?.trim?.() ?? val
            } else if (k in self.syncfield) {
                (ret as any)[k] = self.syncfield[k]
            }
        })
        ret.languageid = this.languageid
        ret.importformatid = this.availableData.selectedImportFormatId()
        if (ret.fieldtype == 'Reference') {
            ret.referenceobjecttypeid = self.referenceobjecttypeid()
            ret.referencefieldid = self.referencefieldid()
        }
        return ret
    }

}